/**
 * Async wrapper for localStorage operations to prevent main thread blocking
 * Provides a promise-based API for storage operations
 */

// Import React hooks for the useAsyncStorage hook
import { useState, useEffect, useCallback } from 'react';

export class AsyncStorage {
  private static cache = new Map<string, string | null>();
  private static initialized = false;

  /**
   * Initialize cache with existing localStorage data
   */
  private static async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    return new Promise((resolve) => {
      // Use timeout to ensure callback fires even if browser is busy
      // Without timeout, requestIdleCallback can wait indefinitely during
      // heavy browser activity (Auth0 redirects, React mounting, etc.)
      requestIdleCallback(
        () => {
          try {
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key) {
                this.cache.set(key, localStorage.getItem(key));
              }
            }
          } catch (error) {
            console.warn('Failed to initialize AsyncStorage cache:', error);
          }
          this.initialized = true;
          resolve();
        },
        { timeout: 1000 }
      );
    });
  }

  /**
   * Get an item from storage asynchronously
   */
  static async getItem(key: string): Promise<string | null> {
    try {
      await this.initialize();

      // Return from cache first for immediate response
      if (this.cache.has(key)) {
        const cachedValue = this.cache.get(key) ?? null;
        return cachedValue;
      }

      // Fallback to async localStorage read
      return new Promise((resolve) => {
        requestIdleCallback(
          () => {
            try {
              const value = localStorage.getItem(key);
              this.cache.set(key, value);
              resolve(value);
            } catch (error) {
              console.error(`Failed to get item ${key}:`, error);
              resolve(null);
            }
          },
          { timeout: 500 }
        );
      });
    } catch (error) {
      console.error(`AsyncStorage getItem error for ${key}:`, error);
      return null;
    }
  }

  /**
   * Set an item in storage asynchronously
   */
  static async setItem(key: string, value: string): Promise<void> {
    await this.initialize();

    // Update cache immediately
    this.cache.set(key, value);

    // Persist to localStorage asynchronously
    return new Promise((resolve, reject) => {
      requestIdleCallback(
        () => {
          try {
            localStorage.setItem(key, value);
            resolve();
          } catch (error) {
            // Revert cache on error
            this.cache.delete(key);
            console.error(`Failed to set item ${key}:`, error);
            reject(error);
          }
        },
        { timeout: 500 }
      );
    });
  }

  /**
   * Remove an item from storage asynchronously
   */
  static async removeItem(key: string): Promise<void> {
    await this.initialize();

    // Update cache immediately
    this.cache.delete(key);

    // Remove from localStorage asynchronously
    return new Promise((resolve) => {
      requestIdleCallback(
        () => {
          try {
            localStorage.removeItem(key);
          } catch (error) {
            console.error(`Failed to remove item ${key}:`, error);
          }
          resolve();
        },
        { timeout: 500 }
      );
    });
  }

  /**
   * Clear all items from storage asynchronously
   */
  static async clear(): Promise<void> {
    // Clear cache immediately
    this.cache.clear();

    // Clear localStorage asynchronously
    return new Promise((resolve) => {
      requestIdleCallback(
        () => {
          try {
            localStorage.clear();
          } catch (error) {
            console.error('Failed to clear storage:', error);
          }
          resolve();
        },
        { timeout: 500 }
      );
    });
  }

  /**
   * Get all keys from storage
   */
  static async keys(): Promise<string[]> {
    await this.initialize();
    return Array.from(this.cache.keys());
  }

  /**
   * Check if a key exists in storage
   */
  static async hasItem(key: string): Promise<boolean> {
    await this.initialize();
    return this.cache.has(key);
  }

  /**
   * Get multiple items at once
   */
  static async multiGet(keys: string[]): Promise<[string, string | null][]> {
    const results = await Promise.all(
      keys.map(async (key) => {
        const value = await this.getItem(key);
        return [key, value] as [string, string | null];
      })
    );
    return results;
  }

  /**
   * Set multiple items at once
   */
  static async multiSet(keyValuePairs: [string, string][]): Promise<void> {
    await Promise.all(
      keyValuePairs.map(([key, value]) => this.setItem(key, value))
    );
  }

  /**
   * Remove multiple items at once
   */
  static async multiRemove(keys: string[]): Promise<void> {
    await Promise.all(keys.map((key) => this.removeItem(key)));
  }
}

// Compatibility layer for gradual migration
export const asyncStorage = AsyncStorage;

// Helper hook for React components
export function useAsyncStorage(key: string, defaultValue?: string) {
  const [value, setValue] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    AsyncStorage.getItem(key)
      .then((storedValue) => {
        setValue(storedValue ?? defaultValue ?? null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [key, defaultValue]);

  const updateValue = useCallback(
    async (newValue: string | null) => {
      try {
        if (newValue === null) {
          await AsyncStorage.removeItem(key);
        } else {
          await AsyncStorage.setItem(key, newValue);
        }
        setValue(newValue);
        setError(null);
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [key]
  );

  return { value, updateValue, loading, error };
}

// Polyfill for older browsers that don't support requestIdleCallback
if (typeof window !== 'undefined' && !window.requestIdleCallback) {
  (
    window as Window & {
      requestIdleCallback?: (callback: IdleRequestCallback) => number;
    }
  ).requestIdleCallback = (callback: IdleRequestCallback) => {
    const start = Date.now();
    const timeoutId = setTimeout(() => {
      callback({
        didTimeout: false,
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
      } as IdleDeadline);
    }, 1);

    // Convert NodeJS.Timeout to number for browser compatibility
    return Number(timeoutId);
  };
}
